import {
    ChangeDetectionStrategy,
    Component,
    InputSignal,
    InputSignalWithTransform,
    OutputEmitterRef,
    WritableSignal,
    booleanAttribute,
    input,
    output,
    signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { IStairType } from '@stair-craft/common/util';

import { IAppLocale, LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';

const BEM_BLOCK: string = 'sc-app-header';

@Component({
    selector: 'sc-app-header',
    templateUrl: './app-header.component.html',
    styleUrl: './app-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        RouterLink,
        RouterLinkActive,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        LanguageSwitcherComponent,
    ],
    host: {
        class: BEM_BLOCK,
        '[class.sc-app-header--over-video]': 'overVideo()',
        '[class.sc-app-header--overlay]': 'overlay()',
        '[class.sc-app-header--menu-open]': 'menuOpen()',
        '(document:keydown.escape)': 'closeMenu()',
    },
})
export class AppHeaderComponent {
    // A phone folds the menu behind the toggle; on a wide window the menu is always in the bar
    protected readonly menuOpen: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly openMenuLabel: string = $localize`:@@navMenuOpen:Открыть меню`;
    protected readonly closeMenuLabel: string = $localize`:@@navMenuClose:Закрыть меню`;

    public readonly currentLocale: InputSignal<string> = input.required<string>();
    public readonly ruHref: InputSignal<string> = input.required<string>();
    public readonly enHref: InputSignal<string> = input.required<string>();
    // Constructor (/calc) hides the "open constructor" CTA — the header keeps a
    // fixed height everywhere, this flag only trims that redundant action.
    public readonly compact: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });
    public readonly activeStairType: InputSignal<IStairType | null> = input<IStairType | null>(null);
    // Over the home hero video the header is transparent; past the video it turns solid again.
    public readonly overVideo: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });
    // The header lies over the page top instead of taking its own row: the home hero starts under it.
    public readonly overlay: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });

    public readonly localeSelected: OutputEmitterRef<IAppLocale> = output<IAppLocale>();

    protected onLocaleSelected(locale: IAppLocale): void {
        this.localeSelected.emit(locale);
    }

    protected toggleMenu(): void {
        this.menuOpen.update((open: boolean): boolean => !open);
    }

    // A tap on any link closes the menu too: a link to the page already open makes no navigation to wait for
    protected closeMenu(): void {
        this.menuOpen.set(false);
    }
}
